import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useAiWallet } from "../../context/AiWallet/AiWalletContext";

function formatCredits(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value);
}

function CreditStat({
  label,
  value,
  emphasize,
  lowBalance,
}: {
  label: string;
  value: number | null | undefined;
  emphasize?: boolean;
  lowBalance?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 88 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: emphasize ? 700 : 600,
          color: lowBalance
            ? "error.main"
            : emphasize
              ? "primary.main"
              : "text.primary",
          lineHeight: 1.3,
        }}
      >
        {formatCredits(value)}
      </Typography>
    </Box>
  );
}

export default function AiDrawerCredits() {
  const { wallet, isLoading } = useAiWallet();

  const allocatedCredits = wallet?.allocatedCredits ?? null;
  const consumedCredits = wallet?.consumedCredits ?? null;
  const balanceCredits = wallet?.balanceCredits ?? null;
  const reservedCredits = wallet?.reservedCredits ?? 0;

  const isBalanceBelowReserved =
    balanceCredits != null && balanceCredits < reservedCredits;
  const isBalanceLow = balanceCredits != null && balanceCredits < 100;

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      {isBalanceBelowReserved ? (
        <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
          Balance credits ({formatCredits(balanceCredits)}) are below reserved
          credits ({formatCredits(reservedCredits)}). AI usage may be limited.
        </Alert>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading AI credits…
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.5,
            flexWrap: "wrap",
          }}
        >
          <CreditStat label="Allocated" value={allocatedCredits} />
          <CreditStat label="Consumed" value={consumedCredits} />
          <CreditStat label="Balance" value={balanceCredits} emphasize lowBalance={isBalanceLow} />
        </Box>
      )}
    </Box>
  );
}
